import { useState } from 'react'
import { Lock, Trash2, Palette, Vibrate, LayoutDashboard, Download, Shield, HelpCircle, MessageSquare, Check } from 'lucide-react'
import { APP_VERSION } from '../lib/version.js'
import { buildGlobalExportZip } from '../lib/export.js'
import { getHapticEnabled, setHapticEnabled } from '../hooks/use-haptic.js'
import { useConfirm } from '../components/boothly/confirm-provider.jsx'
import { useToast } from '../components/boothly/toast-provider.jsx'
import { SectionLabel, SettingsGroup, SettingsRow, SettingsToggleRow, SettingsSegmentedRow, SettingsDivider } from '../components/boothly/settings-ui.jsx'
import { PasswordChangeSheet } from '../settings/password-change-sheet.jsx'
import { AccountDeleteSheet } from '../settings/account-delete.jsx'
import { PrivacySheet, StaticTextSheet } from '../settings/privacy-sheet.jsx'
import { T } from '../theme.jsx'

export function ProfilTab({ authUser, authToken, userStores, userPreferences, onPreferencesChange, dark, themeMode, onThemeModeChange, onLogout }) {
  const confirm = useConfirm()
  const toast = useToast()
  const t = T[dark ? 'dark' : 'light']

  const [hapticOn, setHapticOn] = useState(getHapticEnabled)
  const [pwOpen, setPwOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [privacyOpen, setPrivacyOpen] = useState(false)
  const [helpOpen, setHelpOpen] = useState(false)
  const [exporting, setExporting] = useState(false)

  const email = authUser?.email || '—'
  const initial = (email[0] || '?').toUpperCase()
  const storeCount = (userStores || []).length

  const alwaysDashboard = userPreferences?.always_show_landing === true
  const startStoreId = userPreferences?.start_store_id || null
  const ownStores = (userStores || []).filter(s => s.role !== 'fulfillment')

  const toggleHaptic = () => {
    const next = !hapticOn
    setHapticEnabled(next)
    setHapticOn(next)
  }

  const handleExport = async () => {
    if (exporting) return
    setExporting(true)
    try {
      await buildGlobalExportZip(userStores, authToken)
      toast.success('Export heruntergeladen')
    } catch (e) {
      toast.error(e.message || 'Export fehlgeschlagen')
    } finally {
      setExporting(false)
    }
  }

  const handleLogout = async () => {
    const ok = await confirm({
      title: 'Abmelden?',
      subtitle: 'Du wirst ausgeloggt und kehrst zum Login zurück.',
      confirmLabel: 'Abmelden',
      destructive: true,
    })
    if (ok) onLogout?.()
  }

  return (
    <div className="px-[18px]" style={{ paddingBottom: 'calc(72px + env(safe-area-inset-bottom, 0px))' }}>
      <div className="py-4">
        <h2 className="text-[22px] font-medium">Profil</h2>
      </div>

      {/* Account Card */}
      <div className="bg-[var(--bg-L1)] border border-[var(--border-default)] rounded-2xl p-4 flex items-center gap-4 mb-5">
        <div
          className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 text-[18px] font-semibold"
          style={{ background: 'var(--fill-tertiary)', color: 'var(--label-primary)' }}
        >
          {initial}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[15px] font-medium truncate">{email}</div>
          <div className="text-[13px] text-[var(--label-secondary)] mt-0.5">
            {storeCount} {storeCount === 1 ? 'Bereich' : 'Bereiche'}
          </div>
        </div>
      </div>

      {/* Account */}
      <SectionLabel>Account</SectionLabel>
      <SettingsGroup>
        <SettingsRow icon={<Lock size={18} />} label="Passwort ändern" onClick={() => setPwOpen(true)} />
        <SettingsDivider />
        <SettingsRow icon={<Trash2 size={18} />} label="Konto löschen" destructive onClick={() => setDeleteOpen(true)} />
      </SettingsGroup>

      {/* Darstellung */}
      <SectionLabel>Darstellung</SectionLabel>
      <SettingsGroup>
        <SettingsSegmentedRow
          icon={<Palette size={18} />}
          label="Erscheinungsbild"
          options={[
            { v: 'light', l: 'Hell' },
            { v: 'dark', l: 'Dunkel' },
            { v: 'system', l: 'System' },
          ]}
          value={themeMode}
          onChange={onThemeModeChange}
        />
        <SettingsDivider />
        <SettingsToggleRow icon={<Vibrate size={18} />} label="Haptik" checked={hapticOn} onChange={toggleHaptic} />
      </SettingsGroup>

      {/* Anzeige */}
      <SectionLabel>Anzeige</SectionLabel>
      <SettingsGroup>
        <SettingsToggleRow
          icon={<LayoutDashboard size={18} />}
          label="Immer Dashboard anzeigen"
          sub={alwaysDashboard
            ? 'Aktiv — nach Login immer das Dashboard anzeigen'
            : 'Nach Login direkt in deinen Startbereich'}
          checked={alwaysDashboard}
          onChange={(v) => onPreferencesChange({ always_show_landing: v })}
        />
        {!alwaysDashboard && ownStores.length > 1 && (
          <>
            <SettingsDivider />
            {ownStores.map(s => (
              <SettingsRow
                key={s.id}
                label={s.name}
                trailing={startStoreId === s.id ? <Check size={16} style={{ color: 'var(--accent-online)' }} /> : null}
                onClick={() => onPreferencesChange({ start_store_id: s.id })}
              />
            ))}
          </>
        )}
      </SettingsGroup>

      {/* Daten */}
      <SectionLabel>Daten</SectionLabel>
      <SettingsGroup>
        <SettingsRow
          icon={<Download size={18} />}
          label={exporting ? 'Exportiere…' : 'Vollständiger Daten-Export'}
          sub="ZIP mit allen Stores, Bestellungen, Items"
          onClick={!exporting ? handleExport : undefined}
        />
      </SettingsGroup>
      <p className="text-[12px] text-[var(--label-tertiary)] px-1 mt-1 mb-4">
        Daten werden automatisch in der Cloud gesichert.
      </p>

      {/* Rechtliches */}
      <SectionLabel>Rechtliches</SectionLabel>
      <SettingsGroup>
        <SettingsRow icon={<Shield size={18} />} label="Datenschutz & AGB" onClick={() => setPrivacyOpen(true)} />
        <SettingsDivider />
        <SettingsRow icon={<HelpCircle size={18} />} label="Hilfe / FAQ" onClick={() => setHelpOpen(true)} />
        <SettingsDivider />
        <SettingsRow
          icon={<MessageSquare size={18} />}
          label="Feedback senden"
          onClick={() => { window.location.href = 'mailto:contact@boothly.de?subject=Boothly%20Feedback' }}
        />
      </SettingsGroup>

      <div className="text-center py-3">
        <span className="text-[12px] text-[var(--label-tertiary)]">Version {APP_VERSION}</span>
      </div>

      <button
        onClick={handleLogout}
        className="w-full rounded-2xl py-3.5 text-[15px] font-medium transition-all active:scale-[0.98]"
        style={{ color: 'var(--accent-red)', background: 'var(--bg-L1)', border: '1px solid var(--border-default)' }}
      >
        Abmelden
      </button>

      <div className="h-6" />

      {/* Sub-sheets */}
      {pwOpen && <PasswordChangeSheet dark={dark} onClose={() => setPwOpen(false)} />}
      {deleteOpen && (
        <AccountDeleteSheet dark={dark} t={t}
          authUser={authUser} authToken={authToken}
          onClose={() => setDeleteOpen(false)}
          onDeleted={onLogout} />
      )}
      {privacyOpen && <PrivacySheet dark={dark} t={t} authToken={authToken} onClose={() => setPrivacyOpen(false)} />}
      {helpOpen && (
        <StaticTextSheet dark={dark} t={t}
          title="Hilfe / FAQ"
          body="Folgt bald. Bei Fragen schreib bitte an contact@boothly.de — wir helfen schnell weiter."
          onClose={() => setHelpOpen(false)} />
      )}
    </div>
  )
}
